import {
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Select,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActiveTool } from '../enums/ActiveTool';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { SheetCalibrationButton } from 'src/components/common/forgeViewerInteractions/SheetCalibrationButton';
import { useProtanProjectSteps } from 'src/components/common/ProtanProjectStepsProvider';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import {
  CalibrationUnit,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
} from 'src/gql/graphql';
import { useUserTenant } from 'src/services/auth-info';

export function SheetCalibration() {
  const { t } = useTranslation('project');
  const {
    calibration,
    setActiveTool,
    activeTool,
    drawing: { setDrawingMode },
  } = useSheetViewerV2();
  const { isCalibrating, isComplete, isUpdating, enable, disable } =
    calibration.tool;
  const { register, handleSubmit, reset } = useForm<{
    length: number;
    units: CalibrationUnit;
  }>();

  const { isProtan } = useUserTenant();
  const { setStepStatus, projectSteps } = useProtanProjectSteps();
  const wasAutoEnabled = useRef(false);
  const suppressAutoEnableRef = useRef(false);

  const toggleCalibration = useCallback(() => {
    if (calibration.existing) {
      reset({
        length: calibration.existing?.destinationWidth,
        units: calibration.existing?.destinationUnit,
      });
    }

    // Mark as manually toggled (not auto-enabled)
    wasAutoEnabled.current = false;
    return !isCalibrating ? enable() : disable();
  }, [calibration, isCalibrating, enable, disable, reset]);

  const drawingCalibrationStep = projectSteps?.find(
    (step) => step.step === ProtanProjectStepEnum.DrawingCalibration
  );

  const shouldShowCalibration = useMemo(
    () =>
      isProtan &&
      !calibration.existing &&
      drawingCalibrationStep?.status === ProtanProjectStepStatusEnum.NotStarted,
    [isProtan, calibration.existing, drawingCalibrationStep]
  );

  useEffect(() => {
    // Only auto-enable once per mount when conditions are stable; never auto-disable here
    if (
      shouldShowCalibration &&
      !wasAutoEnabled.current &&
      !isCalibrating &&
      activeTool === ActiveTool.Default &&
      !suppressAutoEnableRef.current
    ) {
      wasAutoEnabled.current = true;
      enable();
    }
  }, [shouldShowCalibration, isCalibrating, activeTool, enable]);

  // Reset suppression once initial auto-enable window ends
  useEffect(() => {
    if (!shouldShowCalibration) {
      suppressAutoEnableRef.current = false;
    }
  }, [shouldShowCalibration]);

  const onSubmit = handleSubmit(async ({ length, units }) => {
    // Prevent auto-enable from re-triggering while we transition away from calibration
    suppressAutoEnableRef.current = true;

    // For Protan, mark steps first to avoid auto re-enable, then jump into drawing
    if (isProtan) {
      wasAutoEnabled.current = false;
      if (shouldShowCalibration) {
        Object.values(ProtanProjectStepEnum).forEach((step) => {
          setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
        });
      }
      setActiveTool(ActiveTool.Drawing);
      setDrawingMode(DrawingMode.MagicPolygon);
    } else {
      // Non-Protan: simply exit calibration
      disable();
    }

    try {
      await calibration.createOrUpdate(length, units);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[SheetCalibration] createOrUpdate failed', error);
    }

    if (shouldShowCalibration && !isProtan) {
      // Set all steps to completed for now
      Object.values(ProtanProjectStepEnum).forEach((step) => {
        setStepStatus(step, ProtanProjectStepStatusEnum.Completed);
      });
    }
  });

  return (
    <Popover isOpen={isCalibrating} closeOnBlur={false} isLazy>
      <PopoverAnchor>
        <SheetCalibrationButton
          hasCalibration={!!calibration.existing}
          onClick={toggleCalibration}
        />
      </PopoverAnchor>
      <PopoverContent borderRadius={'2xl'}>
        <PopoverArrow />
        <PopoverHeader fontWeight="bold">
          {t('project:viewer-interactions.calibrate')}
        </PopoverHeader>
        <PopoverBody>
          {isComplete ? (
            <form id="calibrate-page-form" onSubmit={onSubmit}>
              <Flex gap={2}>
                <FormControl isRequired>
                  <FormLabel>
                    {t('project:viewer-interactions.calibration.length')}
                  </FormLabel>
                  <Input
                    size={'sm'}
                    {...register('length', {
                      valueAsNumber: true,
                    })}
                    type="number"
                    step="any"
                    min="0"
                    autoFocus
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>
                    {t('project:viewer-interactions.calibration.unit')}
                  </FormLabel>
                  <Select
                    size={'sm'}
                    {...register('units')}
                    borderRadius={'3xl'}
                  >
                    <option value={CalibrationUnit.Mm}>
                      {t('millimeters')}
                    </option>
                    <option value={CalibrationUnit.M}>{t('meters')}</option>
                  </Select>
                </FormControl>
              </Flex>
            </form>
          ) : (
            <>{t('project:viewer-interactions.calibration.prompt')}</>
          )}
        </PopoverBody>
        <PopoverFooter>
          <Flex justifyContent={'end'} gap={2}>
            <ButtonGroup>
              <Button
                size={'sm'}
                colorScheme="brand"
                type="submit"
                form="calibrate-page-form"
                isLoading={isUpdating}
                isDisabled={!isComplete}
              >
                {t('project:viewer-interactions.calibrate')}
              </Button>
              {!shouldShowCalibration && (
                <Button
                  size={'sm'}
                  colorScheme={'gray'}
                  onClick={toggleCalibration}
                >
                  {t('project:viewer-interactions.calibration.cancel')}
                </Button>
              )}
            </ButtonGroup>
          </Flex>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
